import { Delete } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PinNumpadProps {
  onDigit: (digit: string) => void;
  onBackspace: () => void;
  disabled?: boolean;
  /**
   * 'light' (padrão): texto escuro sobre fundo claro — usado em telas normais
   * (ex: modal de admin, /criar-pin). 'dark': texto branco translúcido —
   * usado sobre a foto de fundo do login (src/pages/PinLogin.tsx).
   */
  variant?: 'light' | 'dark';
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'backspace'] as const;

/**
 * Teclado numérico customizado (0-9 + apagar), só toques — nunca abre o
 * teclado nativo do dispositivo. Reusado na entrada e na criação do PIN.
 */
export function PinNumpad({ onDigit, onBackspace, disabled, variant = 'light' }: PinNumpadProps) {
  const keyClasses = cn(
    'transition-colors disabled:opacity-40 disabled:pointer-events-none',
    variant === 'dark'
      ? 'text-white hover:bg-white/10 active:bg-white/20'
      : 'text-foreground hover:bg-muted active:bg-muted',
  );

  return (
    <div className="grid grid-cols-3 gap-3 w-full max-w-xs mx-auto" role="group" aria-label="Teclado numérico">
      {KEYS.map((key, index) => {
        if (key === '') {
          return <div key={`spacer-${index}`} aria-hidden="true" />;
        }

        if (key === 'backspace') {
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={onBackspace}
              aria-label="Apagar"
              className={cn('h-14 rounded-full flex items-center justify-center', keyClasses)}
            >
              <Delete className="h-5 w-5" />
            </button>
          );
        }

        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            onClick={() => onDigit(key)}
            aria-label={`Dígito ${key}`}
            className={cn('h-14 rounded-full text-xl font-semibold', keyClasses)}
          >
            {key}
          </button>
        );
      })}
    </div>
  );
}
